import React, { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import ButtonComponent from '../../library/components/button.component';
import { HIERARCHY } from '../../library/constants/constants';
import { regenerateShareLink, sendLastUpdated, validateShareLink, ValidateShareLinkResponse } from '../../services/sharing.service';
import MagicLinkExpiredComponent from './magiclink-expired.component';
import MagicLinkNotActiveComponent from './magiclink-notactive.component';
import MagicLinkRenewComponent from './magiclink-renew.component';
import MagicLinkSentComponent from './magiclink-sent.component';
import MagicLinkValidtatingComponent from './magiclink-validating.component';
import './magiclink.component.scss';

const MagicLinkComponent = () => {
    const queryParams = new URLSearchParams(window.location.search);
    const folderKey: string = queryParams.get('key') as string;
    const [validating, setValidating] = useState<boolean>(true);
    const [tokenExpired, setTokenExpired] = useState<boolean>(false);
    const [expired, setExpired] = useState<boolean>(false);
    const [inactive, setInactive] = useState<boolean>(false);
    const [newLinkSent, setNewLinkSent] = useState<boolean>(false);
    const { showBoundary } = useErrorBoundary();
    const rawParams = window.location.search;
    const token = decodeURIComponent(rawParams.split('token=')[1]?.split('&')[0]);

    useEffect(() => {
        const controller = new AbortController();
        handleValidation(token);

        return () => controller.abort();
    }, [token]);

    const handleRedirectToShare = (folderKey: string) => {
        if (folderKey) {
            try {
                const redirectTo = `/account/magicconsume?token=${token}&key=${folderKey}`;
                window.location.replace(redirectTo);
            } catch (err: any) {
                if (err?.name !== 'AbortError') {
                    console.log('Error redirecting to shared folder', err);
                }
            }
        }
    };

    // go to the login page if the user clicks
    const handleRedirectToLogin = () => {
        window.location.replace('/account/login');
    };

    const updateLastAccess = async (token: string) => {
        try {
            await sendLastUpdated(token);
        } catch (err) {
            console.log(err);
        }
    };

    // validate the token, that it has not expired and that the share is still valid.
    const handleValidation = async (token: string) => {
        try {
            const response: ValidateShareLinkResponse = await validateShareLink(token);
            const { token_expired, share_expired, share_cancelled } = response;
            if (token_expired) {
                setTokenExpired(true);
                return;
            }
            if (share_expired) {
                setExpired(true);
                return;
            }

            if (share_cancelled) {
                setInactive(true);
                return;
            }

            updateLastAccess(token);
            handleRedirectToShare(folderKey);
        } catch (err) {
            console.log(err);
            showBoundary(err as Error);
        } finally {
            setValidating(false);
        }
    };

    // handle renewing the shared link
    const handleRenew = async () => {
        try {
            await regenerateShareLink(token);
            setNewLinkSent(true);
            setValidating(false);
        } catch (err) {
            console.log(err);
        }
    };

    return (
        <div className="magic-link-component">
            <div className="navbar">
                <div className="logo"></div>
                <div className="login">
                    <ButtonComponent
                        type={HIERARCHY.TERTIARY}
                        text="Log in"
                        onClick={() => handleRedirectToLogin()}
                    />
                </div>
            </div>
            <div className="content">
                {validating ? (
                    <MagicLinkValidtatingComponent />
                ) : !newLinkSent ? (
                    <>
                        {tokenExpired && <MagicLinkRenewComponent handleButtonClick={handleRenew} />}
                        {expired && <MagicLinkExpiredComponent />}
                        {inactive && <MagicLinkNotActiveComponent />}
                    </>
                ) : (
                    <MagicLinkSentComponent />
                )}
            </div>
        </div>
    );
};

export default MagicLinkComponent;
